import { useRef, type PointerEvent } from 'react';

import { verticalProgressMove } from '../domain/fox-kifu-presentation';

export function VerticalMoveSlider({
  value,
  max,
  disabled,
  busy = false,
  ariaLabel = '上下拖动查看棋谱',
  onChange,
}: {
  readonly value: number;
  readonly max: number;
  readonly disabled: boolean;
  readonly busy?: boolean;
  readonly ariaLabel?: string;
  readonly onChange: (value: number) => void;
}) {
  const activePointer = useRef<number | null>(null);
  const seek = (event: PointerEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    onChange(verticalProgressMove(event.clientY, bounds.top, bounds.height, max));
  };
  const progress = `${max === 0 ? 0 : (value / max) * 100}%`;
  return (
    <div
      className={`fox-kifu-vertical-slider${busy ? ' is-busy' : ''}`}
      role="slider"
      aria-label={ariaLabel}
      aria-orientation="vertical"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-valuetext={`第 ${value} 手，共 ${max} 手`}
      aria-disabled={disabled}
      aria-busy={busy}
      tabIndex={disabled ? -1 : 0}
      onPointerDown={(event) => {
        if (disabled || event.button !== 0) return;
        event.preventDefault();
        activePointer.current = event.pointerId;
        event.currentTarget.setPointerCapture(event.pointerId);
        seek(event);
      }}
      onPointerMove={(event) => {
        if (disabled || activePointer.current !== event.pointerId) return;
        event.preventDefault();
        seek(event);
      }}
      onPointerUp={(event) => {
        if (activePointer.current !== event.pointerId) return;
        if (!disabled) seek(event);
        activePointer.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      }}
      onPointerCancel={() => {
        activePointer.current = null;
      }}
      onKeyDown={(event) => {
        if (disabled) return;
        const next =
          event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? max
              : event.key === 'ArrowUp' || event.key === 'ArrowRight'
                ? Math.min(max, value + 1)
                : event.key === 'ArrowDown' || event.key === 'ArrowLeft'
                  ? Math.max(0, value - 1)
                  : null;
        if (next === null) return;
        event.preventDefault();
        event.stopPropagation();
        onChange(next);
      }}
    >
      <span className="fox-kifu-slider-track" aria-hidden="true">
        <span className="fox-kifu-slider-fill" style={{ height: progress }} />
        <span className="fox-kifu-slider-thumb" style={{ bottom: progress }} />
      </span>
    </div>
  );
}
